/**
 * The behavior registry — the seam that makes a step body's *algorithmic residues* swappable
 * configuration, the counterpart to the presentational primitive registry (`../primitives/registry`).
 *
 * A "behavior" is a non-presentational kernel a primitive leans on: a scoring engine, a derivation, a
 * parse, a report assembly. Extracting these behind stable string keys means a primitive is declarative
 * (it asks for `deep-dive-scoring` by key rather than importing one specific algorithm), and a campaign
 * — or a theme — can register a different implementation under the same key with zero primitive changes.
 *
 * This module is pure MECHANICS only: it holds the registry map and the register/get/keys functions and
 * imports nothing. The built-in behaviors are registered by `./core` (the jest-safe pure/async kernels)
 * and, for the heavy report subsystem, by `./report` (imported only from the already-lazy report-editor
 * primitive) — keeping the `@react-pdf`/worker-backed graph out of this module and its mechanics test.
 */

/** Any registered kernel. Heterogeneous by nature, so the stored shape is "some function"; callers cast
 * to the real signature through the typed accessors in `./index`. `never[]` params accept any function
 * without resorting to `any`. */
export type Behavior = (...args: never[]) => unknown;

const REGISTRY: Record<string, Behavior> = {};

/** Register (or override) a behavior under `key`. Later registration wins, so a campaign can specialise. */
export function registerBehavior(key: string, behavior: Behavior): void {
  REGISTRY[key] = behavior;
}

/** The behavior registered under `key`, cast to the caller's signature, or null when the key is unknown. */
export function getBehavior<F extends Behavior = Behavior>(key: string): F | null {
  return (REGISTRY[key] as F | undefined) ?? null;
}

/** Like `getBehavior`, but throws when the key is unknown — for a required kernel a primitive cannot render without. */
export function getBehaviorOrThrow<F extends Behavior = Behavior>(key: string): F {
  const behavior = REGISTRY[key];
  if (!behavior) throw new Error(`No behavior registered under "${key}"`);
  return behavior as F;
}

/** Every registered behavior key (for admin pickers and validation). */
export function registeredBehaviorKeys(): string[] {
  return Object.keys(REGISTRY);
}
